import useSWRInfinite from 'swr/infinite';
import { useEffect } from 'react';
import { swrKeys } from '@shared/api/swr/keys';
import type { AuditLogPageDto } from '@shared/api/contracts';
import {
  mapAuditLogPage,
  toExportSearch,
  toListSearch,
  type AuditLogFilters,
} from '@entities/audit-log';

export const AUDIT_LOG_PAGE_SIZE = 50;

export const AUDIT_LOG_SWR_OPTIONS = {
  refreshInterval: 0,
  revalidateOnFocus: false,
  revalidateOnReconnect: false,
  revalidateFirstPage: false,
} as const;

export function useAuditLogList(filters: AuditLogFilters, enabled: boolean) {
  const filterSearch = toExportSearch(filters);

  const result = useSWRInfinite<AuditLogPageDto>(
    (pageIndex, previous: AuditLogPageDto | null) => {
      if (!enabled) return null;
      if (previous && !previous.nextCursor) return null;
      const cursor = pageIndex === 0 ? null : previous?.nextCursor;
      if (pageIndex > 0 && !cursor) return null;
      return swrKeys.auditLog.list(toListSearch(filters, cursor, AUDIT_LOG_PAGE_SIZE));
    },
    AUDIT_LOG_SWR_OPTIONS
  );

  const { setSize, mutate } = result;

  useEffect(() => {
    void setSize(1);
  }, [filterSearch, setSize]);

  const pages = (result.data ?? []).map(mapAuditLogPage);
  const items = pages.flatMap((page) => page.items);
  const period = pages[0]?.period ?? null;
  const nextCursor = pages.length ? pages[pages.length - 1].nextCursor : null;

  const refresh = async () => {
    await setSize(1);
    await mutate();
  };

  return {
    items,
    period,
    hasMore: Boolean(nextCursor),
    isLoading: enabled && !result.data && !result.error,
    isValidating: result.isValidating,
    error: enabled ? result.error : undefined,
    loadMore: () => {
      void result.setSize(result.size + 1);
    },
    refresh,
  };
}
